import { AUTH_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE } from '@constants/common.constants';
import { deleteCookie, getCookieValue, setCookieValue } from '@utils/auth/cookies.util';

/**
 * MP-427 — dual token storage, mirroring `@portal/shared-auth` (miportal monorepo).
 *
 * The shell writes the session and refresh tokens to localStorage as the primary store and keeps a
 * cookie with the same key in parallel for consumers on `.vidacamara.cl`. Reads prefer localStorage
 * and fall back to the cookie; writes and deletes touch both. The public names keep "Cookie" so no
 * consumer changes: it now names the token store, not only the cookie.
 */

const readLocalStorage = (key: string): string | undefined => {
	try {
		// An empty value is no token: fall back to the cookie instead of returning "".
		const value = localStorage.getItem(key);
		if (!value) return undefined;

		return value;
	} catch {
		// localStorage may be unavailable (private mode / disabled): degrade to the cookie.
		return undefined;
	}
};

const writeLocalStorage = (key: string, value: string): void => {
	try {
		localStorage.setItem(key, value);
	} catch {
		// localStorage may be unavailable: the parallel cookie still carries the token.
	}
};

const removeLocalStorage = (key: string): void => {
	try {
		localStorage.removeItem(key);
	} catch {
		// Nothing to remove when localStorage is unavailable.
	}
};

export const getSessionCookie = () => readLocalStorage(AUTH_TOKEN_COOKIE) ?? getCookieValue(AUTH_TOKEN_COOKIE);

export const setSessionCookie = (token: string) => {
	writeLocalStorage(AUTH_TOKEN_COOKIE, token);
	setCookieValue(AUTH_TOKEN_COOKIE, token);
};

export const deleteSessionCookie = () => {
	removeLocalStorage(AUTH_TOKEN_COOKIE);
	deleteCookie(AUTH_TOKEN_COOKIE);
};

export const getRefreshToken = () => readLocalStorage(REFRESH_TOKEN_COOKIE) ?? getCookieValue(REFRESH_TOKEN_COOKIE);

export const setRefreshCookie = (token: string) => {
	writeLocalStorage(REFRESH_TOKEN_COOKIE, token);
	setCookieValue(REFRESH_TOKEN_COOKIE, token);
};

export const deleteRefreshCookie = () => {
	removeLocalStorage(REFRESH_TOKEN_COOKIE);
	deleteCookie(REFRESH_TOKEN_COOKIE);
};
